/**
 * Inactivity Settings Service
 * Manages session timeout configuration, combining admin settings with user preferences.
 * Uses the shared apiClient (Axios) for auth, CSRF, and credentials.
 */

import apiClient from './apiClient';

class InactivitySettingsService {
  constructor() {
    this.cache = new Map();
    this.listeners = new Set();
  }

  async getAdminSettings() {
    try {
      const { data } = await apiClient.get('/api/admin/settings/system');
      return data.data || {};
    } catch (error) {
      console.warn('Failed to fetch admin settings, using defaults:', error);
      return {
        sessionTimeout: 3600,       // 1 hour in seconds
        inactivityWarning: 300,     // 5 minutes in seconds
        enableInactivityWarnings: true,
      };
    }
  }

  async updateAdminSettings(settings) {
    try {
      await apiClient.put('/api/admin/settings/system', {
        sessionTimeout: settings.sessionTimeout,
        inactivityWarning: settings.inactivityWarning,
        enableInactivityWarnings: settings.enableInactivityWarnings,
      });
      this.notifyListeners(settings);
      return true;
    } catch (error) {
      console.error('Failed to update admin settings:', error);
      throw error;
    }
  }

  getUserPreferences() {
    try {
      const stored = localStorage.getItem('inactivityPreferences');
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.warn('Failed to load user preferences:', error);
      return null;
    }
  }

  saveUserPreferences(preferences) {
    try {
      localStorage.setItem('inactivityPreferences', JSON.stringify(preferences));
      this.notifyListeners(preferences);
      return true;
    } catch (error) {
      console.error('Failed to save user preferences:', error);
      return false;
    }
  }

  async getEffectiveConfig(userRole = 'agent') {
    try {
      let adminSettings = {};
      if (userRole === 'agentadmin' || userRole === 'admin') {
        adminSettings = await this.getAdminSettings();
      }

      const userPreferences = this.getUserPreferences();

      const adminTimeout = (adminSettings.sessionTimeout || 3600) * 1000;
      const adminWarning = (adminSettings.inactivityWarning || 300) * 1000;

      const defaultConfig = {
        inactivityLimit: adminTimeout,
        modalCountdown: 60,
        warningThresholds: [
          { time: adminWarning, type: 'subtle' },
          { time: adminTimeout - 2 * 60 * 1000, type: 'prominent' },
          { time: adminTimeout, type: 'critical' },
        ],
        enableWarnings: adminSettings.enableInactivityWarnings !== false,
        enableSound: false,
        autoRefresh: true,
      };

      if (userPreferences?.timeoutSettings) {
        return {
          ...defaultConfig,
          ...userPreferences.timeoutSettings,
          inactivityLimit: Math.min(
            userPreferences.timeoutSettings.inactivityLimit || defaultConfig.inactivityLimit,
            adminTimeout
          ),
        };
      }

      return defaultConfig;
    } catch (error) {
      console.error('Failed to get effective config:', error);
      return {
        inactivityLimit: 15 * 60 * 1000,
        modalCountdown: 60,
        warningThresholds: [
          { time: 10 * 60 * 1000, type: 'subtle' },
          { time: 13 * 60 * 1000, type: 'prominent' },
          { time: 15 * 60 * 1000, type: 'critical' },
        ],
        enableWarnings: true,
        enableSound: false,
        autoRefresh: true,
      };
    }
  }

  validateSettings(settings) {
    const errors = [];

    if (settings.inactivityLimit < 5 * 60 * 1000) {
      errors.push('Inactivity limit must be at least 5 minutes');
    }
    if (settings.inactivityLimit > 2 * 60 * 60 * 1000) {
      errors.push('Inactivity limit cannot exceed 2 hours');
    }
    if (settings.modalCountdown < 10) {
      errors.push('Modal countdown must be at least 10 seconds');
    }
    if (settings.modalCountdown > 300) {
      errors.push('Modal countdown cannot exceed 5 minutes');
    }
    if (settings.warningThresholds) {
      for (const threshold of settings.warningThresholds) {
        if (threshold.time >= settings.inactivityLimit) {
          errors.push('Warning thresholds must be before inactivity limit');
        }
      }
    }

    return { isValid: errors.length === 0, errors };
  }

  addListener(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  notifyListeners(settings) {
    this.listeners.forEach((callback) => {
      try {
        callback(settings);
      } catch (error) {
        console.error('Error in settings listener:', error);
      }
    });
  }

  resetToDefaults() {
    try {
      localStorage.removeItem('inactivityPreferences');
      this.notifyListeners(null);
      return true;
    } catch (error) {
      console.error('Failed to reset settings:', error);
      return false;
    }
  }

  exportSettings() {
    try {
      const data = {
        timestamp: new Date().toISOString(),
        userPreferences: this.getUserPreferences(),
        version: '1.0',
      };
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inactivity-settings-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      console.error('Failed to export settings:', error);
      return false;
    }
  }

  async importSettings(file) {
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (data.userPreferences) {
        this.saveUserPreferences(data.userPreferences);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to import settings:', error);
      throw new Error('Invalid settings file');
    }
  }
}

export default new InactivitySettingsService();
